import { format } from "date-fns";
import { FC, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Bin, Key } from "shared/assets/icons";
import { copy } from "shared/assets";
import {
  APIErrorMessage,
  Button,
  ButtonContainer,
  Card,
  LinkButton,
  Loader,
  Message,
  Modal,
  Table,
} from "shared/components";
import { addTemporaryToast } from "shared/components/Toast";
import { Spacer } from "shared/layout";
import { CodeText } from "shared/styles";
import { IStatus, ITheme } from "shared/types";
import { useApiKey } from "./hooks/useApiKey";
import { GenerateApiKeyModal } from "./modals/GenerateApiKeyModal";

interface ApiKeysProps {
  canManageProfile?: boolean;
}

export const ApiKeys: FC<ApiKeysProps> = () => {
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const { getKeys, hasApiKey, deleteKey } = useApiKey();

  const content = useMemo(() => {
    if (getKeys.isLoading) return <Loader />;
    if (getKeys.isError)
      return (
        <Message status={IStatus.error}>
          <p>{copy.apiKeys.messages.errorFetching}</p>
        </Message>
      );

    if (getKeys.data.length === 0)
      return (
        <Message status={IStatus.info}>
          <p>
            {copy.apiKeys.empty.description.map((line, index) => (
              <span key={index}>
                {line}
                {index < copy.apiKeys.empty.description.length - 1 && <br />}
              </span>
            ))}
          </p>
        </Message>
      );

    return (
      <Spacer size="m">
        <Message status={IStatus.success}>
          {getKeys.data.length === 1
            ? copy.apiKeys.active.title
            : copy.apiKeys.active.titlePlural.replace(
                "{count}",
                getKeys.data.length.toString()
              )}
        </Message>
        <p>
          {(getKeys.data.length === 1
            ? copy.apiKeys.active.description
            : copy.apiKeys.active.descriptionPlural
          ).map((line, index) => (
            <span key={index}>
              {line}
              {index <
                (getKeys.data.length === 1
                  ? copy.apiKeys.active.description.length
                  : copy.apiKeys.active.descriptionPlural.length - 1) && <br />}
            </span>
          ))}
        </p>
        <Table
          columns={[
            {
              Header: copy.apiKeys.table.columns.description,
              accessor: "name",
            },
            {
              Header: copy.apiKeys.table.columns.apiKey,
              accessor: "keyPrefix",
              Cell: ({ value }) => <CodeText>{value}_xxxxxxxxxx</CodeText>,
            },
            {
              Header: copy.apiKeys.table.columns.createdAt,
              accessor: "createdAt",
              Cell: ({ value }) => (
                <span>{format(new Date(value), "dd/MM/yyyy HH:mm")}</span>
              ),
            },
          ]}
          getRowActions={(id: string) => [
            {
              label: copy.apiKeys.table.actions.delete,
              handleClick: () => setDeleteId(id),
              icon: Bin,
            },
          ]}
          data={getKeys.data}
        />
      </Spacer>
    );
  }, [getKeys, setDeleteId]);

  useEffect(() => {
    if (deleteKey.isSuccess) {
      setDeleteId(null);
      deleteKey.reset();

      dispatch(
        addTemporaryToast({
          message: copy.apiKeys.messages.deleteSuccess,
          status: IStatus.success,
        })
      );
    }
  }, [deleteKey, dispatch]);

  return (
    <Card id="api-keys" title={copy.apiKeys.title} icon={Key}>
      <Spacer size="m">
        {isModalOpen && <GenerateApiKeyModal setIsModalOpen={setIsModalOpen} />}
        {deleteId && (
          <Modal
            handleClose={() => setDeleteId(null)}
            title={copy.apiKeys.delete.title}
          >
            <Spacer size="m">
              <p>
                {copy.apiKeys.delete.message.map((line, index) => (
                  <span key={index}>
                    {line}
                    {index < copy.apiKeys.delete.message.length - 1 && <br />}
                  </span>
                ))}
              </p>
              <ButtonContainer>
                <Button
                  status={IStatus.error}
                  onClick={async () => {
                    await deleteKey.mutateAsync(deleteId);
                  }}
                >
                  <Spacer spaceDirection="horizontal" size="xs">
                    <span>{copy.apiKeys.delete.confirm}</span>
                    {deleteKey.isPending && <Loader size="s" />}
                  </Spacer>
                </Button>
                <Button
                  onClick={() => {
                    setDeleteId(null);
                    deleteKey.reset();
                  }}
                  theme={ITheme.secondary}
                >
                  {copy.apiKeys.delete.cancel}
                </Button>
              </ButtonContainer>
              {deleteKey.isError && (
                <APIErrorMessage
                  apiErrorMessage={copy.apiKeys.messages.errorDeleting}
                />
              )}
            </Spacer>
          </Modal>
        )}
        {content}
        <ButtonContainer spaceDirection="horizontal">
          <Button onClick={() => setIsModalOpen(true)}>
            {copy.apiKeys.generate.button}
          </Button>
          {hasApiKey && (
            <LinkButton
              theme={ITheme.secondary}
              target="_blank"
              to={{
                pathname: "https://api.tourmanagement.com/api/docs?ui=re_doc",
              }}
            >
              {copy.apiKeys.actions.viewDocumentation}
            </LinkButton>
          )}
        </ButtonContainer>
      </Spacer>
    </Card>
  );
};
